import { Stack, Theme, Typography } from '@mui/material';
import {
  ApiError,
  GetGroupsQueryFilters,
  Group,
  GROUPS_QUERY_FILTER_KEYS,
  useGetGroupsQuery,
} from '@schooly/api';
import { FC, PropsWithChildren, useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { SelectContentSkeleton } from '../SelectContentSkeleton';
import { SelectSearchInput } from '../SelectSearchInput';
import { pickOnlyParamsFromFilterKeys, toggleMultipleValueArrayProperty } from '../utils';
import { GroupSelectRow } from './GroupSelectRow';
import { GroupTagSelect, GroupTagSelectProps } from './GroupTagSelect';

type GroupExpandedSelectProps = PropsWithChildren<{
  schoolId: string;
  selectedIds: string[];
  invalidGroupIds?: string[];
  onApply: (v: string[]) => void;
  onClear: () => void;
  onClose: () => void;
  filters?: GetGroupsQueryFilters;
  applyImmediate?: boolean;
}>;
export const GroupExpandedSelect: FC<GroupExpandedSelectProps> = ({
  schoolId,
  selectedIds: initialSelectedIds,
  invalidGroupIds,
  onApply,
  onClose,
  onClear,
  filters = {},
  applyImmediate = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);

  const { data, hasNextPage, params, setParams, isLoading, isFetchingNextPage, fetchNextPage } =
    useGetGroupsQuery(
      {
        schoolId,
        query: '',
        filters: pickOnlyParamsFromFilterKeys(GROUPS_QUERY_FILTER_KEYS, filters),
      },
      {
        refetchOnMount: 'always',
      },
    );

  const toggleGroupIds = useCallback(
    (v: string[]) => {
      setSelectedIds((ids) => {
        const newIds = toggleMultipleValueArrayProperty(ids, v);
        if (applyImmediate) {
          onApply(newIds);
        }
        return newIds;
      });
    },
    [applyImmediate, onApply],
  );

  const handleApply = useCallback(() => {
    onApply(selectedIds);
    onClose();
  }, [selectedIds, onApply, onClose]);

  const handleChangeQuery = useCallback(
    (query: string) => {
      setParams((p) => ({ ...p, query }));
    },
    [setParams],
  );

  const renderContent = useCallback(() => {
    if (!data) return <SelectContentSkeleton />;

    const entries =
      data.pages.reduce((prev, curr) => [...prev, ...curr.results], [] as Group[]) ?? [];

    if (!entries.length)
      return (
        <Stack m={1} gap={2}>
          <Typography p={1}>
            <FormattedMessage id="input-NoOptionsFound" />
          </Typography>
          {!applyImmediate && (
            <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
          )}
        </Stack>
      );

    return (
      <Stack mb={2} gap={1}>
        {entries.map((group) => (
          <GroupSelectRow
            onClick={(id) => {
              toggleGroupIds([id]);
              handleChangeQuery('');
            }}
            key={group.id}
            group={group}
            isSelected={selectedIds.includes(group.id)}
          />
        ))}
        {hasNextPage && <SelectContentSkeleton />}
        {!applyImmediate && <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />}
      </Stack>
    );
  }, [
    data,
    handleChangeQuery,
    toggleGroupIds,
    selectedIds,
    handleApply,
    onClear,
    hasNextPage,
    applyImmediate,
  ]);

  const groupTagsSx = (theme: Theme, id: string) => {
    return invalidGroupIds?.length
      ? {
          maxWidth: 200,
          backgroundColor: 'background.paper',
          '.MuiChip-label': {
            color: !!invalidGroupIds?.includes(id)
              ? theme.palette.error.main
              : theme.palette.primary.main,
          },
          borderColor: `${
            !!invalidGroupIds?.includes(id) ? theme.palette.error.main : undefined
          } !important`,
        }
      : {};
  };

  return (
    <ExpandedSelect
      hasSelectedValue={selectedIds.length > 0}
      renderContent={renderContent}
      onClose={onClose}
      onClear={onClear}
      onClickInputArea={() => inputRef.current?.focus()}
      isFetchingNextPage={isLoading || isFetchingNextPage}
      hasNextPage={hasNextPage}
      onFetchNextPage={fetchNextPage}
      showNextPageLoading={false}
      width={500}
    >
      {selectedIds.map((id) => {
        return (
          <GroupTagSelect
            key={id}
            id={id}
            onDelete={() => toggleGroupIds([id])}
            size="small"
            sx={(theme) => {
              return {
                ...groupTagsSx(theme, id),
                maxWidth: 200,
              };
            }}
          />
        );
      })}

      <SelectSearchInput
        ref={inputRef}
        autoFocus
        value={params.query || ''}
        onChangeText={handleChangeQuery}
      />
    </ExpandedSelect>
  );
};

type RenderGroupTagsParams = {
  ids: string[];
  onDelete?: (v: string) => void;
  onClick?: (v: string) => void;
  tagProps?: Omit<GroupTagSelectProps, 'id'> | ((v: string) => Omit<GroupTagSelectProps, 'id'>);
  onError?: (id: string, error: ApiError) => void;
};

export const renderGroupTags = ({
  ids,
  onDelete,
  onClick,
  tagProps,
  onError,
}: RenderGroupTagsParams) =>
  ids.map((v) => (
    <GroupTagSelect
      key={v}
      id={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
      onError={onError}
    />
  ));
